import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { formatDate, formatDateTime, apiErrorMsg } from '@/lib/formatters';
import {
  Clock, LogIn, LogOut, CalendarDays, ChevronLeft, ChevronRight,
  Users, CalendarOff, CheckCircle, XCircle, Timer, Play, Square,
  Plus, Briefcase, Coffee, ThermometerSun, AlertTriangle
} from 'lucide-react';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const ATT_STATUS = {
  present: { label: 'Present', color: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-500' },
  half_day: { label: 'Half Day', color: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' },
  absent: { label: 'Absent', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  leave: { label: 'Leave', color: 'bg-blue-100 text-blue-800', dot: 'bg-blue-500' },
  holiday: { label: 'Holiday', color: 'bg-purple-100 text-purple-800', dot: 'bg-purple-500' },
};

const LEAVE_STATUS = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-700',
};

const LEAVE_TYPES = [
  { value: 'casual', label: 'Casual Leave', icon: Coffee },
  { value: 'sick', label: 'Sick Leave', icon: ThermometerSun },
  { value: 'earned', label: 'Earned Leave', icon: Briefcase },
  { value: 'comp_off', label: 'Comp Off', icon: CalendarOff },
];

export default function AttendancePage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('clock');

  return (
    <div className="space-y-5" data-testid="attendance-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Attendance & Leaves</h1>
        <p className="text-slate-500 text-sm mt-0.5">Track daily hours, view monthly attendance, and manage leaves</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="clock" data-testid="att-clock-tab"><Clock size={14} className="mr-1" /> Clock In/Out</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="att-monthly-tab"><CalendarDays size={14} className="mr-1" /> Monthly View</TabsTrigger>
          <TabsTrigger value="leaves" data-testid="att-leaves-tab"><CalendarOff size={14} className="mr-1" /> Leaves</TabsTrigger>
          {['director', 'manager'].includes(user?.role) && (
            <TabsTrigger value="team" data-testid="att-team-tab"><Users size={14} className="mr-1" /> Team</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="clock"><ClockTab user={user} /></TabsContent>
        <TabsContent value="monthly"><MonthlyTab user={user} /></TabsContent>
        <TabsContent value="leaves"><LeavesTab user={user} /></TabsContent>
        {['director', 'manager'].includes(user?.role) && (
          <TabsContent value="team"><TeamTab /></TabsContent>
        )}
      </Tabs>
    </div>
  );
}

/* ─── Clock In/Out Tab ─── */
function ClockTab({ user }) {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [elapsed, setElapsed] = useState('00:00:00');

  const fetchToday = useCallback(async () => {
    try {
      const { data } = await api.get('/attendance/today');
      setRecord(data.record);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  // Live timer
  useEffect(() => {
    if (!record?.clock_in || record?.clock_out) return;
    const tick = () => {
      const diff = Math.floor((Date.now() - new Date(record.clock_in).getTime()) / 1000);
      const h = String(Math.floor(diff / 3600)).padStart(2, '0');
      const m = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
      const s = String(diff % 60).padStart(2, '0');
      setElapsed(`${h}:${m}:${s}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [record]);

  const clockIn = async () => {
    setActing(true);
    try {
      await api.post('/attendance/clock-in');
      toast.success('Clocked in!');
      fetchToday();
    } catch (err) { toast.error(apiErrorMsg(err)); }
    finally { setActing(false); }
  };

  const clockOut = async () => {
    setActing(true);
    try {
      const { data } = await api.post('/attendance/clock-out');
      toast.success(`Clocked out! Hours worked: ${data.hours_worked}`);
      fetchToday();
    } catch (err) { toast.error(apiErrorMsg(err)); }
    finally { setActing(false); }
  };

  const isClockedIn = record?.clock_in && !record?.clock_out;
  const isClockedOut = record?.clock_in && record?.clock_out;

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="max-w-lg mx-auto space-y-6">
      {/* Status Card */}
      <Card className={`border-2 ${isClockedIn ? 'border-emerald-300 bg-emerald-50/50' : isClockedOut ? 'border-slate-300 bg-slate-50' : 'border-blue-300 bg-blue-50/50'}`}>
        <CardContent className="p-6 text-center">
          <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${isClockedIn ? 'bg-emerald-100' : isClockedOut ? 'bg-slate-200' : 'bg-blue-100'}`}>
            {isClockedIn ? <Timer size={36} className="text-emerald-600 animate-pulse" /> : isClockedOut ? <CheckCircle size={36} className="text-slate-500" /> : <Clock size={36} className="text-blue-600" />}
          </div>
          <h2 className="text-lg font-bold text-slate-800 mb-1">
            {isClockedIn ? 'Currently Working' : isClockedOut ? 'Day Complete' : 'Not Clocked In'}
          </h2>
          {isClockedIn && (
            <p className="text-4xl font-extrabold font-mono text-emerald-700 my-3" data-testid="live-timer">{elapsed}</p>
          )}
          {isClockedOut && (
            <p className="text-3xl font-extrabold font-mono text-slate-700 my-3" data-testid="hours-worked">{record.hours_worked} hrs</p>
          )}
          <div className="text-sm text-slate-500 space-y-1 mt-2">
            {record?.clock_in && <p>Clock In: <span className="font-medium text-slate-700">{new Date(record.clock_in).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' })}</span></p>}
            {record?.clock_out && <p>Clock Out: <span className="font-medium text-slate-700">{new Date(record.clock_out).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' })}</span></p>}
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-3">
        {!record?.clock_in && (
          <Button onClick={clockIn} disabled={acting} className="flex-1 h-14 text-lg bg-emerald-600 hover:bg-emerald-700 rounded-xl" data-testid="clock-in-btn">
            <Play size={22} className="mr-2" /> {acting ? 'Clocking In...' : 'Clock In'}
          </Button>
        )}
        {isClockedIn && (
          <Button onClick={clockOut} disabled={acting} className="flex-1 h-14 text-lg bg-red-600 hover:bg-red-700 rounded-xl" data-testid="clock-out-btn">
            <Square size={22} className="mr-2" /> {acting ? 'Clocking Out...' : 'Clock Out'}
          </Button>
        )}
        {isClockedOut && (
          <div className="flex-1 h-14 rounded-xl bg-slate-100 border-2 border-slate-200 flex items-center justify-center text-slate-500 font-medium">
            <CheckCircle size={20} className="mr-2 text-emerald-500" /> Done for today
          </div>
        )}
      </div>

      {/* Today info */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Today&apos;s Info</p>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div><p className="text-sm font-bold text-slate-800">{user?.name}</p><p className="text-[10px] text-slate-400">Employee</p></div>
            <div><p className="text-sm font-bold text-slate-800">{new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })}</p><p className="text-[10px] text-slate-400">Date</p></div>
            <div>
              <p className="text-sm font-bold text-slate-800">
                {record ? <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${ATT_STATUS[record.status]?.color || 'bg-slate-100'}`}>{ATT_STATUS[record.status]?.label || '-'}</span> : 'No Record'}
              </p>
              <p className="text-[10px] text-slate-400">Status</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ─── Monthly View Tab ─── */
function MonthlyTab({ user }) {
  const [month, setMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; });
  const [data, setData] = useState({ records: [], leaves: [], total_hours: 0, present_days: 0, half_days: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState('');
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (['director', 'manager'].includes(user?.role)) {
      api.get('/users').then(r => setUsers(r.data.users)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    setLoading(true);
    const params = { month };
    if (selectedUser) params.user_id = selectedUser;
    api.get('/attendance/monthly', { params }).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [month, selectedUser]);

  const recordMap = useMemo(() => {
    const m = {};
    data.records.forEach(r => { m[r.date] = r; });
    return m;
  }, [data.records]);

  const [y, m] = month.split('-').map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const firstDay = new Date(y, m - 1, 1).getDay();
  const prevMonth = () => { const d = new Date(y, m - 2, 1); setMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`); };
  const nextMonth = () => { const d = new Date(y, m, 1); setMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`); };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={prevMonth} data-testid="month-prev"><ChevronLeft size={18} /></Button>
          <span className="text-base font-semibold text-slate-800 w-36 text-center">{MONTHS[m-1]} {y}</span>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={nextMonth} data-testid="month-next"><ChevronRight size={18} /></Button>
        </div>
        {['director', 'manager'].includes(user?.role) && users.length > 0 && (
          <Select value={selectedUser} onValueChange={v => setSelectedUser(v === 'self' ? '' : v)}>
            <SelectTrigger className="w-[200px] h-9 text-sm" data-testid="att-user-filter"><SelectValue placeholder="My Attendance" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="self">My Attendance</SelectItem>
              {users.map(u => <SelectItem key={u._id} value={u._id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-slate-200 kpi-card"><CardContent className="p-3">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Total Hours</p>
          <p className="text-xl font-extrabold font-mono text-emerald-700">{data.total_hours} hrs</p>
        </CardContent></Card>
        <Card className="border-slate-200 kpi-card"><CardContent className="p-3">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Present Days</p>
          <p className="text-xl font-extrabold font-mono text-blue-700">{data.present_days}</p>
        </CardContent></Card>
        <Card className="border-slate-200 kpi-card"><CardContent className="p-3">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Half Days</p>
          <p className="text-xl font-extrabold font-mono text-amber-600">{data.half_days}</p>
        </CardContent></Card>
        <Card className="border-slate-200 kpi-card"><CardContent className="p-3">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Leaves</p>
          <p className="text-xl font-extrabold font-mono text-purple-600">{data.leaves.filter(l => l.status === 'approved').length}</p>
        </CardContent></Card>
      </div>

      {/* Calendar Grid */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-7 gap-1">
            {DAYS.map(d => <div key={d} className="text-center text-[10px] font-semibold text-slate-400 uppercase py-1">{d}</div>)}
            {cells.map((day, i) => {
              if (!day) return <div key={`e-${i}`} />;
              const dateStr = `${y}-${String(m).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
              const rec = recordMap[dateStr];
              const st = rec ? ATT_STATUS[rec.status] : null;
              const today = new Date().toISOString().split('T')[0];
              const isToday = dateStr === today;
              const isSunday = new Date(y, m - 1, day).getDay() === 0;
              return (
                <div key={dateStr} className={`min-h-[56px] p-1.5 rounded-lg border text-left ${isToday ? 'border-emerald-400 bg-emerald-50/50' : 'border-slate-100'}`} data-testid={`att-day-${dateStr}`}>
                  <span className={`text-xs font-medium ${isToday ? 'text-emerald-700 font-bold' : isSunday ? 'text-red-400' : 'text-slate-600'}`}>{day}</span>
                  {st && (
                    <div className="mt-0.5">
                      <div className={`w-full px-1 py-0.5 rounded text-[9px] font-medium text-center ${st.color}`}>{st.label}</div>
                      {rec.hours_worked > 0 && <p className="text-[9px] text-slate-400 text-center mt-0.5">{rec.hours_worked}h</p>}
                    </div>
                  )}
                  {!st && isSunday && <div className="mt-0.5 text-[9px] text-red-300 text-center">Off</div>}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 px-1">
        {Object.entries(ATT_STATUS).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1.5 text-xs text-slate-600">
            <div className={`w-3 h-3 rounded-full ${v.dot}`} /> {v.label}
          </div>
        ))}
      </div>

      {/* Daily Breakdown Table */}
      {data.records.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Daily Breakdown</CardTitle></CardHeader>
          <CardContent>
            <div className="table-container">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase font-semibold">Date</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Clock In</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Clock Out</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Hours</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.records.map(r => (
                    <TableRow key={r._id}>
                      <TableCell className="text-sm font-medium">{formatDate(r.date)}</TableCell>
                      <TableCell className="text-sm">{r.clock_in ? new Date(r.clock_in).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
                      <TableCell className="text-sm">{r.clock_out ? new Date(r.clock_out).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
                      <TableCell className="text-sm font-mono font-semibold">{r.hours_worked || '-'}</TableCell>
                      <TableCell><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ATT_STATUS[r.status]?.color || 'bg-slate-100'}`}>{ATT_STATUS[r.status]?.label || r.status}</span></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ─── Leaves Tab ─── */
function LeavesTab({ user }) {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequest, setShowRequest] = useState(false);

  const fetchLeaves = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/leaves');
      setLeaves(data.leaves);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLeaves(); }, [fetchLeaves]);

  const handleApprove = async (id, status) => {
    try {
      await api.put(`/leaves/${id}`, { status });
      toast.success(`Leave ${status}`);
      fetchLeaves();
    } catch (err) { toast.error(apiErrorMsg(err)); }
  };

  const pending = leaves.filter(l => l.status === 'pending').length;
  const approved = leaves.filter(l => l.status === 'approved').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-xs">{leaves.length} total</Badge>
          {pending > 0 && <Badge className="bg-amber-100 text-amber-800 text-xs">{pending} pending</Badge>}
          <Badge className="bg-emerald-100 text-emerald-800 text-xs">{approved} approved</Badge>
        </div>
        <Button onClick={() => setShowRequest(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="request-leave-btn">
          <Plus size={16} /> Request Leave
        </Button>
      </div>

      <div className="table-container bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase font-semibold">Employee</TableHead>
              <TableHead className="text-xs uppercase font-semibold">Type</TableHead>
              <TableHead className="text-xs uppercase font-semibold">From</TableHead>
              <TableHead className="text-xs uppercase font-semibold">To</TableHead>
              <TableHead className="text-xs uppercase font-semibold">Days</TableHead>
              <TableHead className="text-xs uppercase font-semibold">Reason</TableHead>
              <TableHead className="text-xs uppercase font-semibold">Status</TableHead>
              {['director', 'manager'].includes(user?.role) && <TableHead className="text-xs uppercase font-semibold">Action</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? Array.from({ length: 3 }).map((_, i) => (
              <TableRow key={i}>{Array.from({ length: 8 }).map((_, j) => <TableCell key={j}><div className="h-4 bg-slate-100 rounded animate-pulse" /></TableCell>)}</TableRow>
            )) : leaves.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-400">
                <CalendarOff size={32} className="mx-auto mb-2 text-slate-300" />No leave records
              </TableCell></TableRow>
            ) : leaves.map(l => (
              <TableRow key={l._id} data-testid={`leave-row-${l._id}`}>
                <TableCell className="text-sm font-medium">{l.user_name}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs capitalize">{l.type?.replace('_', ' ')}</Badge></TableCell>
                <TableCell className="text-sm">{formatDate(l.start_date)}</TableCell>
                <TableCell className="text-sm">{formatDate(l.end_date)}</TableCell>
                <TableCell className="text-sm font-semibold text-center">{l.days}</TableCell>
                <TableCell className="text-xs text-slate-500 max-w-[150px] truncate">{l.reason || '-'}</TableCell>
                <TableCell><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${LEAVE_STATUS[l.status] || 'bg-slate-100'}`}>{l.status}</span></TableCell>
                {['director', 'manager'].includes(user?.role) && (
                  <TableCell>
                    {l.status === 'pending' && (
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" className="h-7 text-emerald-600" onClick={() => handleApprove(l._id, 'approved')} data-testid={`approve-leave-${l._id}`}><CheckCircle size={14} /></Button>
                        <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => handleApprove(l._id, 'rejected')} data-testid={`reject-leave-${l._id}`}><XCircle size={14} /></Button>
                      </div>
                    )}
                    {l.status !== 'pending' && <span className="text-xs text-slate-400">By {l.approved_by_name || '-'}</span>}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {showRequest && <LeaveRequestDialog open onClose={() => setShowRequest(false)} onCreated={() => { fetchLeaves(); setShowRequest(false); }} />}
    </div>
  );
}

function LeaveRequestDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ type: 'casual', start_date: '', end_date: '', reason: '' });
  const [saving, setSaving] = useState(false);

  const days = useMemo(() => {
    if (!form.start_date) return 0;
    const end = form.end_date || form.start_date;
    try {
      const s = new Date(form.start_date);
      const e = new Date(end);
      return Math.max(1, Math.round((e - s) / 86400000) + 1);
    } catch { return 1; }
  }, [form.start_date, form.end_date]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/leaves', { ...form, end_date: form.end_date || form.start_date });
      toast.success('Leave request submitted');
      onCreated();
    } catch (err) { toast.error(apiErrorMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Request Leave</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Leave Type</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {LEAVE_TYPES.map(lt => (
                <button type="button" key={lt.value}
                  onClick={() => setForm({ ...form, type: lt.value })}
                  className={`flex items-center gap-2 p-3 rounded-lg border text-sm font-medium transition-all ${form.type === lt.value ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                  data-testid={`leave-type-${lt.value}`}
                >
                  <lt.icon size={16} /> {lt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>From Date *</Label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} required data-testid="leave-start-date" /></div>
            <div><Label>To Date</Label><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} data-testid="leave-end-date" /></div>
          </div>
          {days > 0 && <p className="text-sm text-slate-500">Duration: <strong className="text-slate-700">{days} day{days > 1 ? 's' : ''}</strong></p>}
          <div><Label>Reason</Label><Textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} rows={2} placeholder="Reason for leave..." data-testid="leave-reason" /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-leave-btn">{saving ? 'Submitting...' : 'Submit Request'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Team Tab (Director/Manager) ─── */
function TeamTab() {
  const [month, setMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; });
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get('/attendance/team', { params: { month } }).then(r => { setTeam(r.data.team); setLoading(false); }).catch(() => setLoading(false));
  }, [month]);

  const [y, m] = month.split('-').map(Number);
  const prevMonth = () => { const d = new Date(y, m - 2, 1); setMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`); };
  const nextMonth = () => { const d = new Date(y, m, 1); setMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`); };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={prevMonth}><ChevronLeft size={18} /></Button>
        <span className="text-base font-semibold text-slate-800 w-36 text-center">{MONTHS[m-1]} {y}</span>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={nextMonth}><ChevronRight size={18} /></Button>
      </div>

      <div className="table-container bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase font-semibold">Employee</TableHead>
              <TableHead className="text-xs uppercase font-semibold">Role</TableHead>
              <TableHead className="text-xs uppercase font-semibold">Present Days</TableHead>
              <TableHead className="text-xs uppercase font-semibold">Total Hours</TableHead>
              <TableHead className="text-xs uppercase font-semibold">Avg Hours/Day</TableHead>
              <TableHead className="text-xs uppercase font-semibold">Leaves</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>{Array.from({ length: 6 }).map((_, j) => <TableCell key={j}><div className="h-4 bg-slate-100 rounded animate-pulse" /></TableCell>)}</TableRow>
            )) : team.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No team data</TableCell></TableRow>
            ) : team.map(t => (
              <TableRow key={t.user_id} data-testid={`team-row-${t.user_id}`}>
                <TableCell className="font-semibold text-sm">{t.name}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs capitalize">{t.role}</Badge></TableCell>
                <TableCell className="text-sm font-mono font-semibold text-emerald-700">{t.present_days}</TableCell>
                <TableCell className="text-sm font-mono">{t.total_hours} hrs</TableCell>
                <TableCell className="text-sm font-mono">{t.present_days > 0 ? (t.total_hours / t.present_days).toFixed(1) : '0'} hrs</TableCell>
                <TableCell className="text-sm">{t.leaves > 0 ? <Badge className="bg-blue-100 text-blue-800 text-xs">{t.leaves}</Badge> : '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
